import React, { memo, useCallback } from 'react';
import { BlurItem } from '../../items/blur/blur-item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { NumberControlUpdateHandler } from '@/features/remotion-editor/editor/inspector/controls/number-controls';
import { useTranslations } from 'next-intl';
import { ValueInputField } from '@crabs-ai/ds-react';
import { BlurOn } from '@crabs-ai/web-icons';

const BlurAmountControlsUnmemoized: React.FC<{
  blurAmount: number;
  itemId: string;
}> = ({ blurAmount, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();

  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<BlurItem, number>(
    itemId,
    (item) => item.blurAmount,
  );

  const setBlurAmount = useCallback(
    (newBlurAmount: number, commitToUndoStack: boolean) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const prev = i as BlurItem;
            if (prev.blurAmount === newBlurAmount) {
              return prev;
            }
            return {
              ...prev,
              blurAmount: newBlurAmount,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const onValueChange: NumberControlUpdateHandler = useCallback(
    (update) => {
      setBlurAmount(update.num, update.commitToUndoStack);
    },
    [setBlurAmount],
  );

  return (
    <div className="flex-1">
      <ValueInputField
        isUnit="px"
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <BlurOn className="size-[20px] stroke-sys-cont-neutral-default" />
        }
        setValue={onValueChange}
        value={blurAmount}
        min={0}
        max={100}
        step={1}
        accessibilityLabel="Blur Amount"
        hasMixedValue={isMixed}
        className="w-full"
      />
    </div>
  );
};

export const BlurAmountControls = memo(BlurAmountControlsUnmemoized);
